import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { MESSAGING_ENABLED } from "../../constants/features";

interface MessagingEnabledRouteProps {
  children: ReactNode;
}

/**
 * Keeps anyone out of the messaging section who is not meant to be in it — because the feature is
 * switched off in this build, or because their organization was onboarded without SMS messaging.
 *
 * Hiding the nav entry is not enough on its own: a bookmark, a stale tab, or a pasted link would
 * still land on a section whose every action fails, which reads as a broken app rather than a
 * feature they do not have.
 *
 * Renders children whenever the flag is anything other than an explicit `false`. A profile that is
 * still loading, or an older API that does not send the field yet, must not have messaging stripped
 * from it — during a rollout that would look identical to every organization being switched off.
 */
export const MessagingEnabledRoute = ({ children }: MessagingEnabledRouteProps) => {
  const { user } = useAuth();

  // The build switch first: while messaging is off, nobody reaches it regardless of their profile.
  if (!MESSAGING_ENABLED) {
    return <Navigate to="/players" replace />;
  }

  if (user?.profile?.messagingEnabled === false) {
    return <Navigate to="/players" replace />;
  }

  return <>{children}</>;
};
